import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../store'
import { getLandingPageDetails } from '../store/landingSlice'
import type { ILandingDetailsRequest, ILandingResponse } from '../types'

const COOLDOWN_MS = 45000
const DEFAULT_POLL_AFTER_MS = 1200
const DEFAULT_MAX_ATTEMPTS = 5
const BACKOFF_MS = 800

function requestKey(req: ILandingDetailsRequest | null | undefined): string {
  if (req == null) return ''
  const lat = req.latitude ?? ''
  const lng = req.longitude ?? ''
  const r = req.radius ?? ''
  return `${lat},${lng},${r}`
}

function hasLocation(req: ILandingDetailsRequest | null | undefined): boolean {
  if (req == null) return false
  const lat = req.latitude
  const lng = req.longitude
  return typeof lat === 'number' && typeof lng === 'number'
}

/**
 * Conditional landing poll: when response is empty and ingestion was triggered,
 * poll the landing endpoint a few times with backoff. Stops when restaurants
 * appear or max attempts reached. One active cycle per request key; cooldown
 * to avoid repeated immediate polls.
 */
export function useLandingPoll(
  request: ILandingDetailsRequest | null | undefined,
  landingDetail: ILandingResponse | undefined,
): void {
  const dispatch = useDispatch<AppDispatch>()
  const cancelledRef = useRef(false)
  const cooldownRef = useRef<{ key: string; until: number }>({ key: '', until: 0 })
  const pollInProgressRef = useRef(false)

  const shouldStartPoll = useCallback(() => {
    if (!landingDetail || !request) return false
    if (landingDetail.restaurantCount > 0) return false
    if (landingDetail.ingestionTriggered !== true) return false
    if (!hasLocation(request)) return false
    const key = requestKey(request)
    const now = Date.now()
    if (cooldownRef.current.key === key && now < cooldownRef.current.until) return false
    return true
  }, [landingDetail, request])

  useEffect(() => {
    if (!shouldStartPoll() || !request) return
    if (pollInProgressRef.current) return

    const key = requestKey(request)
    cooldownRef.current = { key, until: Date.now() + COOLDOWN_MS }
    cancelledRef.current = false
    pollInProgressRef.current = true

    const pollAfterMs =
      landingDetail?.pollAfterMs ?? DEFAULT_POLL_AFTER_MS
    const maxAttempts =
      landingDetail?.maxPollAttempts ?? DEFAULT_MAX_ATTEMPTS

    let attempt = 0
    let timeoutId: ReturnType<typeof setTimeout>

    const runAttempt = (): void => {
      if (cancelledRef.current) return
      const delay =
        attempt === 0
          ? pollAfterMs
          : pollAfterMs + attempt * BACKOFF_MS

      const runPoll = async (): Promise<void> => {
        if (cancelledRef.current) return
        try {
          const result = await dispatch(getLandingPageDetails(request))
          if (cancelledRef.current) return
          const fulfilled =
            getLandingPageDetails.fulfilled.match(result) &&
            result.payload &&
            (result.payload as ILandingResponse).restaurantCount > 0
          if (fulfilled) {
            pollInProgressRef.current = false
            return
          }
        } catch {
          // ignore; will stop at max attempts
        }
        attempt++
        if (attempt < maxAttempts) runAttempt()
        else pollInProgressRef.current = false
      }

      timeoutId = setTimeout(() => {
        void runPoll()
      }, delay)
    }

    runAttempt()

    return () => {
      cancelledRef.current = true
      pollInProgressRef.current = false
      clearTimeout(timeoutId)
    }
  }, [shouldStartPoll, request, landingDetail, dispatch])
}

/** True when we are in the window where polling may run (empty + ingestionTriggered). */
export function useLandingPollingActive(
  landingDetail: ILandingResponse | undefined,
  request: ILandingDetailsRequest | null | undefined,
): boolean {
  if (!landingDetail || !request) return false
  if (landingDetail.restaurantCount > 0) return false
  if (landingDetail.ingestionTriggered !== true) return false
  return hasLocation(request)
}
